<template>
  <PullToRefresh />
  <!-- ── Global loading ── -->
  <main v-if="loader">
    <div class="loading">
      <span class="loader"></span>
    </div>
    <Footer />
  </main>

  <!-- ── App ── -->
  <main v-else>

    <!-- website_status == 1: countdown OR open content -->
    <section v-if="website_status == 1">

      <!-- Countdown active -->
      <section v-if="timeRemaining">
        <div class="countdown">
          <div class="countdown-math-bg" aria-hidden="true">
          </div>

          <div class="logo_home">
            <img src="@/assets/images/favicon.png" alt="logo" />
            <span class="d-block name">أ/ طارق عبدالغني عبدالباري</span>
            <span class="d-block title_name">مدرس الرياضيات للمرحلة الإعدادية والثانوية</span>
          </div>

          <div class="home-title-start" v-html="websiteTexts.data.home_title"></div>

          <h4 class="time-countdown">يوم
            {{ getArabicDayName(date) }}
            الموافق <span>{{ date }} الساعة {{ formatTo12Hour(time) }}</span>
          </h4>

          <div class="home-title-start" v-html="websiteTexts.data.home_text"></div>

          <div class="timer">
            <p><span>{{ timeRemaining.days }}</span><span>يوم</span></p>
            <p><span>{{ timeRemaining.hours }}</span><span>ساعة</span></p>
            <p><span>{{ timeRemaining.minutes }}</span><span>دقيقة</span></p>
            <p><span>{{ timeRemaining.seconds }}</span><span>ثانية</span></p>
          </div>
        </div>
        <Footer />
      </section>

      <!-- Countdown finished: show app -->
      <section v-if="!timeRemaining">
        <navbar />
        <router-view v-slot="{ Component }">
          <transition name="page-fade" mode="out-in">
            <component :is="Component" :key="$route.path" />
          </transition>
        </router-view>
        <Footer />
        <BottomNav />
      </section>

    </section>

    <!-- website_status != 1: normal open -->
    <section v-else>
      <navbar />
      <router-view v-slot="{ Component }">
        <transition name="page-fade" mode="out-in">
          <component :is="Component" :key="$route.path" />
        </transition>
      </router-view>
      <Footer />
      <BottomNav />
    </section>

    <!-- Floating WhatsApp -->
    <a
      ref="whatsappButton"
      href="https://wa.me/+201280194879"
      target="_blank"
      class="floating-whatsapp"
      :class="{ 'is-dragging': whatsappDrag.active }"
      :style="whatsappStyle"
      aria-label="WhatsApp"
      flow="up"
      tooltip="للتواصل عبر الواتساب"
      @pointerdown="startWhatsappDrag"
      @click="handleWhatsappClick"
    >
      <img src="@/assets/images/whatsapp-icon.png" alt="WhatsApp" />
    </a>

  </main>
</template>

<script>
import axios from 'axios'
import Swal from 'sweetalert2'
import navbar from '@/components/layout/navbar.vue'
import Footer from './components/layout/footer.vue'
import BottomNav from '@/components/layout/BottomNav.vue'
import PullToRefresh from '@/components/common/PullToRefresh.vue'
import { websiteTexts } from './websiteTexts';

export default {
  name: 'App',
  components: { navbar, Footer, BottomNav, PullToRefresh },
  data() {
    return {
      targetDate: "",
      timeRemaining: { days: 0, hours: 0, minutes: 0, seconds: 0 },
      website_status: 0,
      loader: true,
      statusLoaded: false,
      timeLoaded: false,
      date: null,
      time: null,
      current_date: null,
      current_time: null,
      serverNow: null,
      whatsappPosition: null,
      whatsappDrag: {
        active: false,
        moved: false,
        pointerId: null,
        offsetX: 0,
        offsetY: 0,
      },
    };
  },
  computed: {
    websiteTexts() { return websiteTexts; },
    whatsappStyle() {
      if (!this.whatsappPosition || !this.isMobileViewport()) return {};
      return {
        left: `${this.whatsappPosition.x}px`,
        top: `${this.whatsappPosition.y}px`,
        right: "auto",
        bottom: "auto",
      };
    },
  },
  methods: {
    isMobileViewport() {
      return window.innerWidth <= 768;
    },
    getWhatsappBounds() {
      const button = this.$refs.whatsappButton;
      const width = button?.offsetWidth || 52;
      const height = button?.offsetHeight || 52;
      const viewport = window.visualViewport;
      const viewportWidth = viewport?.width || window.innerWidth;
      const viewportHeight = viewport?.height || window.innerHeight;
      return {
        width,
        height,
        minX: 8,
        minY: 8,
        maxX: Math.max(8, viewportWidth - width - 8),
        maxY: Math.max(8, viewportHeight - height - 8),
      };
    },
    clampWhatsappPosition(position) {
      const bounds = this.getWhatsappBounds();
      return {
        x: Math.min(Math.max(position.x, bounds.minX), bounds.maxX),
        y: Math.min(Math.max(position.y, bounds.minY), bounds.maxY),
      };
    },
    defaultWhatsappPosition() {
      const bounds = this.getWhatsappBounds();
      const bottomNav = document.querySelector(".app-bnav");
      const navRect = bottomNav?.getBoundingClientRect();
      const navHeight = navRect ? Math.max(0, window.innerHeight - navRect.top) : 0;
      const safeBottom = Math.max(82, navHeight + 16);
      return this.clampWhatsappPosition({
        x: bounds.maxX,
        y: Math.max(bounds.minY, (window.visualViewport?.height || window.innerHeight) - bounds.height - safeBottom),
      });
    },
    loadWhatsappPosition() {
      if (!this.isMobileViewport()) return;
      try {
        const saved = JSON.parse(localStorage.getItem("floating-whatsapp-position") || "null");
        this.whatsappPosition = saved ? this.clampWhatsappPosition(saved) : this.defaultWhatsappPosition();
      } catch (e) {
        this.whatsappPosition = this.defaultWhatsappPosition();
      }
    },
    saveWhatsappPosition() {
      if (this.whatsappPosition) {
        localStorage.setItem("floating-whatsapp-position", JSON.stringify(this.whatsappPosition));
      }
    },
    startWhatsappDrag(event) {
      if (!this.isMobileViewport()) return;
      const button = this.$refs.whatsappButton;
      if (!button) return;
      if (!this.whatsappPosition) this.whatsappPosition = this.defaultWhatsappPosition();
      const rect = button.getBoundingClientRect();
      this.whatsappDrag = {
        active: true,
        moved: false,
        pointerId: event.pointerId,
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
      };
      button.setPointerCapture?.(event.pointerId);
      window.addEventListener("pointermove", this.moveWhatsappDrag, { passive: false });
      window.addEventListener("pointerup", this.endWhatsappDrag);
      window.addEventListener("pointercancel", this.endWhatsappDrag);
    },
    moveWhatsappDrag(event) {
      if (!this.whatsappDrag.active || event.pointerId !== this.whatsappDrag.pointerId) return;
      event.preventDefault();
      const nextPosition = this.clampWhatsappPosition({
        x: event.clientX - this.whatsappDrag.offsetX,
        y: event.clientY - this.whatsappDrag.offsetY,
      });
      const current = this.whatsappPosition || nextPosition;
      if (Math.abs(nextPosition.x - current.x) > 2 || Math.abs(nextPosition.y - current.y) > 2) {
        this.whatsappDrag.moved = true;
      }
      this.whatsappPosition = nextPosition;
    },
    endWhatsappDrag(event) {
      if (event?.pointerId && event.pointerId !== this.whatsappDrag.pointerId) return;
      this.$refs.whatsappButton?.releasePointerCapture?.(this.whatsappDrag.pointerId);
      window.removeEventListener("pointermove", this.moveWhatsappDrag);
      window.removeEventListener("pointerup", this.endWhatsappDrag);
      window.removeEventListener("pointercancel", this.endWhatsappDrag);
      if (this.whatsappDrag.active) this.saveWhatsappPosition();
      this.whatsappDrag.active = false;
    },
    handleWhatsappClick(event) {
      if (this.whatsappDrag.moved) {
        event.preventDefault();
        this.whatsappDrag.moved = false;
      }
    },
    clampWhatsappOnResize() {
      if (!this.isMobileViewport()) {
        this.whatsappPosition = null;
        return;
      }
      this.whatsappPosition = this.whatsappPosition
        ? this.clampWhatsappPosition(this.whatsappPosition)
        : this.defaultWhatsappPosition();
    },
    updateTimeRemaining() {
      const diff = this.targetDate - this.serverNow;
      if (diff <= 0) {
        this.timeRemaining = null;
        clearInterval(this.timer);
        return;
      }
      this.timeRemaining = {
        days:    Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours:   Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
      };
      this.$emit('update-time-remaining', this.timeRemaining);
      this.serverNow = new Date(this.serverNow.getTime() + 1000);
      this.current_date = this.serverNow.toISOString().slice(0, 10);
      this.current_time = this.serverNow.toTimeString().slice(0, 8);
    },

    getArabicDayName(dateStr) {
      if (!dateStr) return '';
      const days = ['الأحد','الاثنين','الثلاثاء','الأربعاء','الخميس','الجمعة','السبت'];
      return days[new Date(dateStr).getDay()];
    },

    formatTo12Hour(timeStr) {
      if (!timeStr) return '';
      const [hour, minute] = timeStr.split(':');
      let h = parseInt(hour, 10);
      let period = '';
      if (h >= 12 && h < 15) { period = 'ظهرًا'; h = h % 12 || 12; }
      else if (h < 12) { period = 'صباحًا'; }
      else { period = 'مساءً'; h = h % 12 || 12; }
      return `${h}:${minute} ${period}`;
    },

    fetchWebStatus() {
      axios.get('/get_website_status')
        .then((response) => {
          if (response.data.status) {
            this.website_status = response.data.website_status;
            localStorage.setItem('website_status', response.data.website_status);
          }
        })
        .catch((error) => {
          Swal.fire({ position: "center", icon: "error", title: error.response?.data?.message });
        })
        .finally(() => {
          this.statusLoaded = true;
          if (this.statusLoaded && this.timeLoaded) this.loader = false;
        });
    },

    fetchWebTexts() {
      axios.get('/website_texts')
        .then((response) => {
          const texts = response.data.website_texts;
          texts.home_title = texts.home_title?.replace(/&nbsp;/g, ' ') || '';
          texts.home_text  = texts.home_text?.replace(/&nbsp;/g, ' ') || '';
          texts.close_text_website = texts.close_text_website?.replace(/&nbsp;/g, ' ') || '';
          websiteTexts.data = texts;

          this.date = response.data.website_start.date;
          this.time = response.data.website_start.time;
          this.targetDate = new Date(`${this.date}T${this.time}`);
          this.current_date = response.data.current_date;
          this.current_time = response.data.current_time;
          this.serverNow = new Date(`${this.current_date}T${this.current_time}`);

          this.updateTimeRemaining();
          this.timer = setInterval(this.updateTimeRemaining, 1000);

          websiteTexts.loaded = true;
          this.timeLoaded = true;
          if (this.statusLoaded && this.timeLoaded) this.loader = false;
        })
        .catch(() => {
          Swal.fire({
            position: "center", icon: "error",
            title: "تعذر تحميل بيانات الموقع. تأكد من اتصال الإنترنت وحاول مرة أخرى.",
            showConfirmButton: true, confirmButtonText: "إعادة المحاولة"
          }).then(() => { this.fetchWebTexts(); });
        });
    },
  },
  mounted() {
    this.fetchWebStatus();
    this.fetchWebTexts();
    this.$nextTick(this.loadWhatsappPosition);
    window.addEventListener("resize", this.clampWhatsappOnResize);
    window.visualViewport?.addEventListener("resize", this.clampWhatsappOnResize);
  },
  beforeUnmount() {
    clearInterval(this.timer);
    window.removeEventListener("resize", this.clampWhatsappOnResize);
    window.visualViewport?.removeEventListener("resize", this.clampWhatsappOnResize);
    this.endWhatsappDrag();
  },
}
</script>
